import React, {Dispatch, SetStateAction, useCallback, useContext} from 'react';

import {Select, SelectInterface} from '@veneer/core';

import {GlobalTranslationsContext} from '../../i18n';

export type SelectFilterProps<
    K extends string,
    T extends number | string | boolean,
    F extends Partial<Record<K, T>>,
> = {
    label: string;
    field: K;
    filters: F;
    setFilters: Dispatch<SetStateAction<F>>;
} & Omit<SelectInterface<T>, 'label' | 'clearIcon' | 'value' | 'onChange' | 'onClear' | 'i18n'>;

const SelectFilter = <K extends string, T extends number | string | boolean, F extends Partial<Record<K, T>>>({
    label,
    filters,
    setFilters,
    field,
    ...selectProps
}: SelectFilterProps<K, T, F>): JSX.Element => {
    const {veneer: v} = useContext(GlobalTranslationsContext);
    const handleChange = useCallback<Required<SelectInterface<T>>['onChange']>(
        option =>
            setFilters(filters => ({
                ...filters,
                [field]: option.value,
            })),
        [setFilters, field]
    );
    const handleClear = useCallback<Required<SelectInterface>['onClear']>(
        () =>
            setFilters(filters => ({
                ...filters,
                [field]: undefined,
            })),
        [setFilters, field]
    );
    return (
        <Select
            label={label}
            clearIcon
            value={filters[field] === undefined ? [] : [filters[field] as T]}
            onChange={handleChange}
            onClear={handleClear}
            i18n={v.select}
            {...selectProps}
        />
    );
};

export default SelectFilter;
